import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CalendarPlus, CheckCircle, XCircle, Search, Link2, AlertTriangle,
  User, Clock, X, Edit, Trash2, Mail, Send, Save, History, Pause, PlayCircle, Archive, FileText
} from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import Sidebar from "@/components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AnagraficaPosatoriBox({ calendars, onPickCalendar }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ denominazione: "", telefono: "", tipo: "posatore", calendar_id: "" });

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/anagrafica-posatori`, { withCredentials: true });
      setItems(res.data?.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchItems(); }, []);

  const resetForm = () => { setForm({ denominazione: "", telefono: "", tipo: "posatore", calendar_id: "" }); setEditingId(null); setShowForm(false); };

  const save = async () => {
    if (!form.denominazione.trim()) { toast.error("Denominazione obbligatoria"); return; }
    const cal = calendars.find((c) => c.id === form.calendar_id);
    const payload = { ...form, calendar_name: cal ? cal.summary : "" };
    try {
      if (editingId) {
        await axios.put(`${API}/anagrafica-posatori/${editingId}`, payload, { withCredentials: true });
        toast.success("Anagrafica aggiornata");
      } else {
        await axios.post(`${API}/anagrafica-posatori`, payload, { withCredentials: true });
        toast.success("Anagrafica salvata");
      }
      resetForm();
      fetchItems();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const edit = (it) => { setEditingId(it.id); setForm({ denominazione: it.denominazione || "", telefono: it.telefono || "", tipo: it.tipo || "posatore", calendar_id: it.calendar_id || "" }); setShowForm(true); };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare "${it.denominazione}"?`)) return;
    try {
      await axios.delete(`${API}/anagrafica-posatori/${it.id}`, { withCredentials: true });
      toast.success("Eliminato");
      fetchItems();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const writableCals = calendars.filter((c) => c.access_role === "owner" || c.access_role === "writer");

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-semibold flex items-center gap-2">
          <User className="w-4 h-4 text-slate-500" />
          Anagrafica Posatori e Vettori
          {!loading && <span className="text-xs font-normal text-slate-500">({items.length})</span>}
        </Label>
        <Button type="button" size="sm" variant="outline" onClick={() => { if (showForm) resetForm(); else setShowForm(true); }} data-testid="toggle-posatore-form">
          {showForm ? <><X className="w-3 h-3 mr-1" />Chiudi</> : <>+ Nuovo</>}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white border border-slate-200 rounded p-3 mb-2 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-semibold">Denominazione *</Label>
              <Input value={form.denominazione} onChange={(e) => setForm({ ...form, denominazione: e.target.value })} placeholder="Es: Mario Rossi / Trasporti XYZ" data-testid="posatore-denominazione" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Telefono</Label>
              <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+39 ..." data-testid="posatore-telefono" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-semibold">Tipo</Label>
              <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-sm" data-testid="posatore-tipo">
                <option value="posatore">Posatore</option>
                <option value="vettore">Vettore / Trasportatore</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Calendario associato</Label>
              <select value={form.calendar_id} onChange={(e) => setForm({ ...form, calendar_id: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-sm" data-testid="posatore-calendar">
                <option value="">— Nessuno —</option>
                {writableCals.map((c) => (
                  <option key={c.id} value={c.id}>{c.summary}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" size="sm" onClick={resetForm} data-testid="posatore-cancel">Annulla</Button>
            <Button type="button" size="sm" onClick={save} data-state="colored" style={{ backgroundColor: "#2563eb" }} className="text-white hover:opacity-90" data-testid="posatore-save">
              {editingId ? "Aggiorna" : "Salva"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs italic text-slate-500">Caricamento...</p>
      ) : items.length === 0 ? (
        <p className="text-xs italic text-slate-500">Nessun posatore o vettore in anagrafica. Clicca "+ Nuovo" per aggiungerne uno.</p>
      ) : (
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {items.map((it) => {
            const cal = calendars.find((c) => c.id === it.calendar_id);
            return (
              <div key={it.id} className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1.5 text-sm" data-testid={`posatore-${it.id}`}>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${it.tipo === "vettore" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                  {it.tipo === "vettore" ? "VETTORE" : "POSATORE"}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-800 truncate">{it.denominazione}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {it.telefono || "—"}
                    {cal && (
                      <> · <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: cal.background_color || "#64748b" }} />{cal.summary}</span></>
                    )}
                    {!cal && it.calendar_name && <> · <em>{it.calendar_name}</em></>}
                  </p>
                </div>
                {it.calendar_id && (
                  <button type="button" onClick={() => onPickCalendar(it.calendar_id)} className="text-xs text-blue-600 hover:underline bg-transparent px-1" title="Seleziona il suo calendario per l'evento" data-testid={`posatore-pick-${it.id}`}>
                    + Calendario
                  </button>
                )}
                <button type="button" onClick={() => edit(it)} className="text-xs text-slate-600 hover:text-slate-800 bg-transparent px-1" title="Modifica" data-testid={`posatore-edit-${it.id}`}>
                  <Edit className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => remove(it)} className="text-xs text-red-600 hover:text-red-800 bg-transparent px-1" title="Elimina" data-testid={`posatore-delete-${it.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CalendariRapidiPage({ user, logout }) {
  const [gmailStatus, setGmailStatus] = useState({ connected: false });
  const [calendars, setCalendars] = useState([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [selectedCalendars, setSelectedCalendars] = useState([]);

  // Contact search
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [recipient, setRecipient] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef(null);

  // Event form
  const [titolo, setTitolo] = useState("");
  const [descrizione, setDescrizione] = useState("");
  // Smaltimento Vecchio
  const [smaltitori, setSmaltitori] = useState([]);
  const [smaltimento, setSmaltimento] = useState({ previsto: false, smaltitore_id: "", note: "" });

  // Nominativo manuale (Ex Novo)
  const [manualMode, setManualMode] = useState(false);
  const [manualForm, setManualForm] = useState({
    nome: "", cognome: "", telefono: "", email: "",
    via: "", civico: "", citta: "", piano: "", note: ""
  });

  // Articoli Ordinati del cliente (popolato solo se recipient viene dal CRM)
  const [articoliCliente, setArticoliCliente] = useState([]);
  const [articoliLoading, setArticoliLoading] = useState(false);
  const [articoliCommessaId, setArticoliCommessaId] = useState(null);
  const fetchSmaltitori = async () => {
    try {
      const res = await axios.get(`${API}/anagrafica-posatori`, { withCredentials: true });
      setSmaltitori(res.data?.items || []);
    } catch { setSmaltitori([]); }
  };
  const [data, setData] = useState("");
  const [oraInizio, setOraInizio] = useState("");
  const [oraFine, setOraFine] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null); // crm_id in modifica

  // Conflict dialog
  const [conflicts, setConflicts] = useState([]);
  const [showConflicts, setShowConflicts] = useState(false);

  // Recent events
  const [recent, setRecent] = useState([]);
  const [suspended, setSuspended] = useState([]);

  // Cancel dialog
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelNotify, setCancelNotify] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  // Resume dialog
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumeTarget, setResumeTarget] = useState(null);
  const [resumeData, setResumeData] = useState("");
  const [resumeOraInizio, setResumeOraInizio] = useState("");
  const [resumeOraFine, setResumeOraFine] = useState("");
  const [resumeCalendars, setResumeCalendars] = useState([]);
  const [resuming, setResuming] = useState(false);

  // Suspend confirm
  const [suspending, setSuspending] = useState(null); // id in progress

  // Notifica dialog
  const [notificaOpen, setNotificaOpen] = useState(false);
  const [notificaEvent, setNotificaEvent] = useState(null);
  const [notificaSubject, setNotificaSubject] = useState("");
  const [notificaBody, setNotificaBody] = useState("");
  const [notificaDest, setNotificaDest] = useState("");
  const [notificaLoading, setNotificaLoading] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [sendingNotifica, setSendingNotifica] = useState(false);

  const fetchGmailStatus = async () => {
    try {
      const res = await axios.get(`${API}/oauth/gmail/status`, { withCredentials: true });
      setGmailStatus(res.data || { connected: false });
      if (res.data?.connected) {
        fetchCalendars();
        fetchRecent();
        fetchSuspended();
        fetchSmaltitori();
      }
    } catch { setGmailStatus({ connected: false }); }
  };

  const fetchCalendars = async () => {
    setLoadingCalendars(true);
    try {
      const res = await axios.get(`${API}/calendar/list`, { withCredentials: true });
      setCalendars(res.data?.items || []);
    } catch {
      toast.error("Errore caricamento calendari");
      setCalendars([]);
    } finally { setLoadingCalendars(false); }
  };

  const fetchRecent = async () => {
    try {
      const res = await axios.get(`${API}/calendar/recent-events?limit=50`, { withCredentials: true });
      setRecent(res.data?.items || []);
    } catch { setRecent([]); }
  };

  const fetchSuspended = async () => {
    try {
      const res = await axios.get(`${API}/calendar/suspended-events?limit=100`, { withCredentials: true });
      setSuspended(res.data?.items || []);
    } catch { setSuspended([]); }
  };

  const refreshAllLists = () => { fetchRecent(); fetchSuspended(); };

  useEffect(() => { fetchGmailStatus(); }, []);

  const connectGmail = async () => {
    try {
      const r = await axios.get(`${API}/oauth/gmail/login?return_to=/calendari-rapidi`, { withCredentials: true });
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore collegamento Google");
    }
  };

  const toggleCalendar = (cid) => {
    setSelectedCalendars((prev) =>
      prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid]
    );
  };

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/contacts/search`, { params: { q, limit: 15 }, withCredentials: true });
        setResults(res.data?.results || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => searchTimeout.current && clearTimeout(searchTimeout.current);
  }, [query]);

  const buildPrefilledTitle = (r) => {
    const g = (k) => String(r[k] || "").trim();
    const base = [`${g("nome")} ${g("cognome")}`.trim(), g("citta"), g("via"), g("civico"), g("piano") && `piano ${g("piano")}`]
      .filter(Boolean).join(", ");
    return [base, g("telefono")].filter(Boolean).join(" ");
  };

  const pickRecipient = (r) => {
    setRecipient(r);
    setQuery(`${r.cognome} ${r.nome}`.trim());
    setResults([]);
    setTitolo(buildPrefilledTitle(r));
  };

  // Carica Articoli Ordinati del cliente CRM selezionato (non per i manuali Ex Novo)
  useEffect(() => {
    if (!recipient || recipient.source === "manuale" || !recipient.id) {
      setArticoliCliente([]);
      setArticoliCommessaId(null);
      return;
    }
    let cancelled = false;
    setArticoliLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/calendar/articoli-cliente`, {
          params: { id: recipient.id, source: recipient.source || "intervista" },
          withCredentials: true,
        });
        if (cancelled) return;
        setArticoliCliente(res.data?.articoli || []);
        setArticoliCommessaId(res.data?.commessa_id || null);
      } catch (e) {
        if (!cancelled) { setArticoliCliente([]); setArticoliCommessaId(null); }
      } finally {
        if (!cancelled) setArticoliLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [recipient]);

  const clearRecipient = () => {
    setRecipient(null);
    setQuery("");
    setResults([]);
  };

  const resetForm = () => {
    clearRecipient();
    setTitolo("");
    setDescrizione("");
    setData("");
    setOraInizio("");
    setOraFine("");
    setSelectedCalendars([]);
    setEditId(null);
    setSmaltimento({ previsto: false, smaltitore_id: "", note: "" });
    setManualMode(false);
    setManualForm({ nome: "", cognome: "", telefono: "", email: "", via: "", civico: "", citta: "", piano: "", note: "" });
  };

  const buildFinalDescrizione = () => {
    let d = (descrizione || "").trim();
    const recNote = (recipient && recipient.note ? String(recipient.note).trim() : "");
    if (recNote) d = d ? `${d}\n\n${recNote}` : recNote;
    if (smaltimento.previsto) {
      const s = smaltitori.find((x) => x.id === smaltimento.smaltitore_id);
      let smaltLine = "**Previsto Smaltimento: Il Posatore chiami lo Smaltitore appena è possibile fare ritiro**";
      if (s) smaltLine += `\n${s.denominazione}${s.telefono ? " · " + s.telefono : ""}`;
      d = d ? `${d}\n\n${smaltLine}` : smaltLine;
    }
    const note = (smaltimento.note || "").trim();
    if (note) d = d ? `${d}\n\n${note}` : note;
    return d;
  };

  const buildPayload = (force) => ({
    calendar_ids: selectedCalendars,
    titolo: titolo.trim(),
    descrizione: buildFinalDescrizione(),
    data,
    ora_inizio: oraInizio,
    ora_fine: oraFine || null,
    force,
    recipient: recipient ? {
      id: recipient.id, source: recipient.source,
      nome: recipient.nome || "", cognome: recipient.cognome || "",
      email: recipient.email || "", telefono: recipient.telefono || "",
      via: recipient.via || "", civico: recipient.civico || "",
      citta: recipient.citta || "",
      piano: recipient.piano || "", note: recipient.note || "",
    } : null,
  });

  const createOrUpdate = async (force = false) => {
    if (selectedCalendars.length === 0) { toast.error("Seleziona almeno un calendario"); return; }
    if (!titolo.trim()) { toast.error("Inserisci un titolo"); return; }
    if (!data || !oraInizio) { toast.error("Imposta data e ora inizio"); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/calendar/quick-event/${editId}`, buildPayload(force), { withCredentials: true });
        toast.success("Evento aggiornato");
      } else {
        const res = await axios.post(`${API}/calendar/quick-event`, buildPayload(force), { withCredentials: true });
        const n = res.data?.created?.length || 0;
        toast.success(`Evento creato su ${n} calendari${force ? " (forzato)" : ""}`);
      }
      setShowConflicts(false);
      setConflicts([]);
      resetForm();
      refreshAllLists();
    } catch (e) {
      if (e?.response?.status === 409) {
        setConflicts(e.response.data?.detail?.conflicts || []);
        setShowConflicts(true);
      } else {
        toast.error(e?.response?.data?.detail || "Errore salvataggio evento");
      }
    } finally { setSaving(false); }
  };

  const checkConflicts = async () => {
    if (selectedCalendars.length === 0) { toast.error("Seleziona almeno un calendario"); return; }
    if (!data || !oraInizio) { toast.error("Imposta data e ora inizio"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/calendar/freebusy-check`,
        { calendar_ids: selectedCalendars, data, ora_inizio: oraInizio, ora_fine: oraFine || null },
        { withCredentials: true });
      const c = res.data?.conflicts || [];
      if (c.length === 0) { toast.success("Nessun conflitto rilevato"); }
      else { setConflicts(c); setShowConflicts(true); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore verifica conflitti");
    } finally { setSaving(false); }
  };

  const editRecent = (ev) => {
    setEditId(ev.id);
    setTitolo(ev.titolo || "");
    setDescrizione(ev.descrizione || "");
    setData(ev.data || "");
    setOraInizio(ev.ora_inizio || "");
    setOraFine(ev.ora_fine || "");
    setSelectedCalendars(ev.calendar_ids || []);
    if (ev.recipient) {
      setRecipient(ev.recipient);
      setQuery(`${ev.recipient.cognome || ""} ${ev.recipient.nome || ""}`.trim());
      if (ev.recipient.source === "manuale") {
        setManualMode(true);
        setManualForm({
          nome: ev.recipient.nome || "",
          cognome: ev.recipient.cognome || "",
          telefono: ev.recipient.telefono || "",
          email: ev.recipient.email || "",
          via: ev.recipient.via || "",
          civico: ev.recipient.civico || "",
          citta: ev.recipient.citta || "",
          piano: ev.recipient.piano || "",
          note: ev.recipient.note || "",
        });
      }
    } else {
      clearRecipient();
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyManualRecipient = () => {
    if (!manualForm.cognome.trim() && !manualForm.nome.trim()) {
      toast.error("Inserisci almeno Cognome o Nome");
      return;
    }
    const r = {
      id: `manual-${Date.now()}`,
      source: "manuale",
      nome: manualForm.nome.trim(),
      cognome: manualForm.cognome.trim(),
      telefono: manualForm.telefono.trim(),
      email: manualForm.email.trim(),
      via: manualForm.via.trim(),
      civico: manualForm.civico.trim(),
      citta: manualForm.citta.trim(),
      piano: manualForm.piano.trim(),
      note: manualForm.note.trim(),
    };
    setRecipient(r);
    setQuery(`${r.cognome} ${r.nome}`.trim());
    setResults([]);
    setTitolo(buildPrefilledTitle(r));
  };

  const toggleManualMode = () => {
    if (manualMode) {
      // chiusura: reset tutto il manuale
      setManualMode(false);
      setManualForm({ nome: "", cognome: "", telefono: "", email: "", via: "", civico: "", citta: "", piano: "", note: "" });
      if (recipient && recipient.source === "manuale") {
        clearRecipient();
      }
    } else {
      setManualMode(true);
      // pulisco eventuale ricerca
      if (recipient && recipient.source !== "manuale") {
        clearRecipient();
      }
    }
  };

  const openCancelDialog = (ev) => {
    setCancelTarget(ev);
    const hasEmail = !!(ev?.recipient?.email);
    setCancelNotify(hasEmail);
    setCancelOpen(true);
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await axios.delete(`${API}/calendar/quick-event/${cancelTarget.id}`, {
        params: { notify_client: cancelNotify },
        withCredentials: true,
      });
      toast.success(cancelNotify ? "Evento cancellato e cliente avvisato via email" : "Evento cancellato");
      if (editId === cancelTarget.id) resetForm();
      setCancelOpen(false);
      setCancelTarget(null);
      refreshAllLists();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore cancellazione");
    } finally { setCancelling(false); }
  };

  const suspendEvent = async (ev) => {
    if (!window.confirm(`Sospendere l'evento "${ev.titolo}"? Verrà parcheggiato al 31/12/2030 mantenendo tutti i dati.`)) return;
    setSuspending(ev.id);
    try {
      await axios.post(`${API}/calendar/quick-event/${ev.id}/suspend`, {}, { withCredentials: true });
      toast.success("Evento sospeso. Lo trovi nella sezione Eventi Sospesi.");
      if (editId === ev.id) resetForm();
      refreshAllLists();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore sospensione");
    } finally { setSuspending(null); }
  };

  const openResumeDialog = (ev) => {
    setResumeTarget(ev);
    const orig = ev.suspended_original || {};
    setResumeData("");
    setResumeOraInizio(orig.ora_inizio || "");
    setResumeOraFine(orig.ora_fine || "");
    setResumeCalendars(ev.calendar_ids || orig.calendar_ids || []);
    setResumeOpen(true);
  };

  const confirmResume = async () => {
    if (!resumeTarget) return;
    if (!resumeData || !resumeOraInizio) { toast.error("Imposta nuova data e ora inizio"); return; }
    if (resumeCalendars.length === 0) { toast.error("Seleziona almeno un calendario"); return; }
    setResuming(true);
    try {
      await axios.post(`${API}/calendar/quick-event/${resumeTarget.id}/resume`, {
        data: resumeData,
        ora_inizio: resumeOraInizio,
        ora_fine: resumeOraFine || null,
        calendar_ids: resumeCalendars,
      }, { withCredentials: true });
      toast.success("Evento recuperato e riprogrammato");
      setResumeOpen(false);
      setResumeTarget(null);
      refreshAllLists();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore recupero");
    } finally { setResuming(false); }
  };

  const toggleResumeCalendar = (cid) => {
    setResumeCalendars((prev) => prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid]);
  };

  const openNotifica = async (ev) => {
    setNotificaEvent(ev);
    setNotificaOpen(true);
    setNotificaLoading(true);
    try {
      const res = await axios.get(`${API}/calendar/notifica-preview/${ev.id}`, { withCredentials: true });
      setNotificaSubject(res.data?.subject || "");
      setNotificaBody(res.data?.body || "");
      setNotificaDest(res.data?.destinatario || "");
    } catch (e) {
      toast.error("Errore caricamento anteprima");
    } finally { setNotificaLoading(false); }
  };

  const sendNotifica = async () => {
    if (!notificaDest.trim()) { toast.error("Inserisci email destinatario"); return; }
    if (!notificaSubject.trim()) { toast.error("Inserisci oggetto"); return; }
    setSendingNotifica(true);
    try {
      await axios.post(`${API}/calendar/notifica-send/${notificaEvent.id}`, {
        subject: notificaSubject,
        body: notificaBody,
        destinatario: notificaDest.trim(),
      }, { withCredentials: true });
      toast.success("Notifica inviata al cliente e in copia a info@showroomingenio.it");
      setNotificaOpen(false);
      refreshAllLists();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally { setSendingNotifica(false); }
  };

  const saveNotificaTemplate = async () => {
    setSavingTpl(true);
    try {
      await axios.put(`${API}/email-templates/calendari_rapidi_notifica`, {
        titolo: "Notifica Appuntamento Calendari Rapidi",
        subject: notificaSubject,
        body: notificaBody,
      }, { withCredentials: true });
      toast.success("Template salvato. Verrà usato per le prossime notifiche.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio template");
    } finally { setSavingTpl(false); }
  };

  const calendarName = (cid) => {
    const c = calendars.find((x) => x.id === cid);
    return c ? c.summary : cid;
  };
  const fmtIt = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
                <CalendarPlus className="w-8 h-8 text-blue-600" />
                Calendari Rapidi
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Crea rapidamente un evento su uno o più calendari Google scegliendo il nominativo dal CRM
              </p>
            </div>
            {gmailStatus.connected ? (
              <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-lg text-sm" data-testid="gcalendar-status-connected">
                <CheckCircle className="w-4 h-4" />
                <span>Google Calendar collegato</span>
              </div>
            ) : (
              <Button type="button" onClick={connectGmail} data-state="colored" style={{ backgroundColor: "#2563eb" }} className="text-white hover:opacity-90" data-testid="gcalendar-connect-btn">
                <Link2 className="w-4 h-4 mr-2" />
                Collega GCalendar
              </Button>
            )}
          </div>

          {!gmailStatus.connected ? (
            <Card>
              <CardContent className="py-8 text-center">
                <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <p className="text-slate-700 mb-4">Collega Google Calendar per iniziare a creare eventi dal CRM.</p>
                <Button type="button" onClick={connectGmail} data-state="colored" style={{ backgroundColor: "#2563eb" }} className="text-white hover:opacity-90">
                  <Link2 className="w-4 h-4 mr-2" />
                  Collega adesso
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between gap-2">
                    <span>{editId ? "Modifica Evento" : "Nuovo Evento Rapido"}</span>
                    {editId && (
                      <Button type="button" variant="outline" size="sm" onClick={resetForm} data-testid="cancel-edit-btn">
                        <X className="w-3 h-3 mr-1" /> Annulla modifica
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-sm font-semibold">Nominativo CRM</Label>
                      <Button type="button" size="sm" variant="outline" onClick={toggleManualMode} className={manualMode ? "border-blue-400 bg-blue-50 text-blue-700" : ""} data-testid="toggle-manual-recipient">
                        {manualMode ? <><X className="w-3 h-3 mr-1" />Chiudi Ex Novo</> : <>+ Nuovo Ex Novo</>}
                      </Button>
                    </div>
                    {!manualMode && (
                    <div className="relative mt-1">
                      <div className="flex items-center gap-2 bg-white border border-slate-300 rounded px-2 py-1">
                        <Search className="w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          value={query}
                          onChange={(e) => { setQuery(e.target.value); if (recipient) setRecipient(null); }}
                          placeholder="Cerca per cognome, nome, email, telefono..."
                          className="flex-1 outline-none text-sm py-1 bg-transparent"
                          data-testid="contact-search-input"
                        />
                        {query && (
                          <button type="button" onClick={clearRecipient} className="bg-transparent text-slate-400 hover:text-slate-600" data-testid="contact-search-clear">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      {results.length > 0 && !recipient && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto z-10">
                          {results.map((r) => (
                            <button
                              key={`${r.source}-${r.id}`}
                              type="button"
                              onClick={() => pickRecipient(r)}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 flex flex-col text-sm bg-transparent"
                              data-testid={`contact-pick-${r.id}`}
                            >
                              <span className="font-medium text-slate-800">{r.cognome} {r.nome}</span>
                              <span className="text-xs text-slate-500">{r.email} · {r.telefono} <span className="italic text-slate-400">({r.source})</span></span>
                            </button>
                          ))}
                        </div>
                      )}
                      {searching && query.length >= 2 && results.length === 0 && (
                        <p className="text-xs text-slate-400 italic mt-1">Ricerca in corso...</p>
                      )}
                    </div>
                    )}
                    {manualMode && (
                      <div className="mt-2 bg-blue-50/40 border border-blue-200 rounded-lg p-3 space-y-2">
                        <p className="text-xs font-medium text-blue-800 flex items-center gap-1">
                          <User className="w-3 h-3" /> Nuovo nominativo Ex Novo (non salvato in anagrafica, solo per questo evento)
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs font-semibold">Cognome</Label>
                            <Input value={manualForm.cognome} onChange={(e) => setManualForm((p) => ({ ...p, cognome: e.target.value }))} placeholder="Rossi" className="mt-0.5" data-testid="manual-cognome" />
                          </div>
                          <div>
                            <Label className="text-xs font-semibold">Nome</Label>
                            <Input value={manualForm.nome} onChange={(e) => setManualForm((p) => ({ ...p, nome: e.target.value }))} placeholder="Mario" className="mt-0.5" data-testid="manual-nome" />
                          </div>
                          <div>
                            <Label className="text-xs font-semibold">Telefono</Label>
                            <Input value={manualForm.telefono} onChange={(e) => setManualForm((p) => ({ ...p, telefono: e.target.value }))} placeholder="+39 ..." className="mt-0.5" data-testid="manual-telefono" />
                          </div>
                          <div>
                            <Label className="text-xs font-semibold">Mail</Label>
                            <Input value={manualForm.email} onChange={(e) => setManualForm((p) => ({ ...p, email: e.target.value }))} placeholder="nome@email.it" className="mt-0.5" data-testid="manual-email" />
                          </div>
                        </div>
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-12 sm:col-span-6">
                            <Label className="text-xs font-semibold">Via</Label>
                            <Input value={manualForm.via} onChange={(e) => setManualForm((p) => ({ ...p, via: e.target.value }))} placeholder="Via Roma" className="mt-0.5" data-testid="manual-via" />
                          </div>
                          <div className="col-span-6 sm:col-span-2">
                            <Label className="text-xs font-semibold">Civico</Label>
                            <Input value={manualForm.civico} onChange={(e) => setManualForm((p) => ({ ...p, civico: e.target.value }))} placeholder="12/A" className="mt-0.5" data-testid="manual-civico" />
                          </div>
                          <div className="col-span-6 sm:col-span-4">
                            <Label className="text-xs font-semibold">Città</Label>
                            <Input value={manualForm.citta} onChange={(e) => setManualForm((p) => ({ ...p, citta: e.target.value }))} placeholder="Cagliari" className="mt-0.5" data-testid="manual-citta" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs font-semibold">Piano</Label>
                            <Input value={manualForm.piano} onChange={(e) => setManualForm((p) => ({ ...p, piano: e.target.value }))} placeholder="PT / 1° / 3°" className="mt-0.5" data-testid="manual-piano" />
                          </div>
                          <div className="sm:col-span-2">
                            <Label className="text-xs font-semibold">Note</Label>
                            <Input value={manualForm.note} onChange={(e) => setManualForm((p) => ({ ...p, note: e.target.value }))} placeholder="Es: suonare al cancello, parcheggio interno..." className="mt-0.5" data-testid="manual-note" />
                          </div>
                        </div>
                        <div className="flex justify-end pt-1">
                          <Button type="button" size="sm" onClick={applyManualRecipient} data-state="colored" style={{ backgroundColor: "#2563eb" }} className="text-white hover:opacity-90" data-testid="manual-apply">
                            <CheckCircle className="w-3 h-3 mr-1" /> Usa questo nominativo
                          </Button>
                        </div>
                        <p className="text-[11px] text-slate-500 italic">
                          💡 Il titolo evento verrà precompilato come: <code>Posatore posa / Trasportatore consegna + Nome Cognome, Città, Via, Civico, piano Telefono</code>. Le Note vengono aggiunte in automatico nella descrizione dell'evento.
                        </p>
                      </div>
                    )}
                    {recipient && (
                      <div className="mt-2 bg-blue-50 border border-blue-200 rounded px-3 py-2 flex items-center gap-2 text-sm flex-wrap">
                        <User className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">{recipient.cognome} {recipient.nome}</span>
                        {recipient.source === "manuale" && <span className="text-[10px] bg-blue-600 text-white px-1 py-0.5 rounded uppercase">Ex Novo</span>}
                        <span className="text-slate-500 text-xs">{recipient.email}</span>
                        {recipient.telefono && <span className="text-slate-500 text-xs">· {recipient.telefono}</span>}
                        {(recipient.via || recipient.citta) && (
                          <span className="text-slate-500 text-xs">· {recipient.via} {recipient.civico} {recipient.citta}</span>
                        )}
                        {recipient.piano && <span className="text-slate-500 text-xs">· Piano {recipient.piano}</span>}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-sm font-semibold">Data</Label>
                      <Input type="date" value={data} onChange={(e) => setData(e.target.value)} className="mt-1" data-testid="event-date" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Ora inizio</Label>
                      <Input type="time" value={oraInizio} onChange={(e) => setOraInizio(e.target.value)} className="mt-1" data-testid="event-time-start" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Ora fine <span className="text-xs text-slate-400 font-normal">(opz. +1h)</span></Label>
                      <Input type="time" value={oraFine} onChange={(e) => setOraFine(e.target.value)} className="mt-1" data-testid="event-time-end" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-semibold">Calendari su cui scrivere <span className="text-xs text-slate-400 font-normal">(selezione multipla)</span></Label>
                    {loadingCalendars ? (
                      <p className="text-sm italic text-slate-500 mt-2">Caricamento calendari...</p>
                    ) : calendars.length === 0 ? (
                      <p className="text-sm italic text-slate-500 mt-2">Nessun calendario trovato</p>
                    ) : (
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto border border-slate-200 rounded p-2 bg-slate-50">
                        {calendars.filter((c) => c.access_role === "owner" || c.access_role === "writer").map((c) => (
                          <label key={c.id} className="flex items-center gap-2 cursor-pointer bg-white px-2 py-1.5 rounded border border-slate-200 hover:border-blue-300">
                            <input
                              type="checkbox"
                              checked={selectedCalendars.includes(c.id)}
                              onChange={() => toggleCalendar(c.id)}
                              className="w-4 h-4 accent-blue-600"
                              data-testid={`calendar-check-${c.id}`}
                            />
                            <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c.background_color || "#64748b" }} />
                            <span className="text-sm text-slate-800 truncate">{c.summary}</span>
                            {c.primary && (<span className="text-[10px] bg-blue-100 text-blue-700 px-1 py-0.5 rounded">PRINCIPALE</span>)}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <AnagraficaPosatoriBox
                    calendars={calendars}
                    onPickCalendar={(cid) => { if (cid && !selectedCalendars.includes(cid)) setSelectedCalendars((p) => [...p, cid]); }}
                  />

                  <div>
                    <Label className="text-sm font-semibold">Titolo Evento</Label>
                    <Input value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder="Es: Mario Rossi, Cagliari, Via Roma, 12, piano 2 333..." className="mt-1" data-testid="event-title" />
                    {titolo.trim() && selectedCalendars.length > 0 && (
                      <div className="mt-2 space-y-0.5" data-testid="event-title-preview">
                        <p className="text-xs font-semibold text-slate-600">Titolo su ogni calendario:</p>
                        {selectedCalendars.map((cid) => {
                          const o = smaltitori.find((x) => x.calendar_id === cid);
                          const cal = calendars.find((c) => c.id === cid);
                          const full = o ? `${o.denominazione} ${o.tipo === "vettore" ? "consegna" : "posa"} ${titolo.trim()}` : `posa ${titolo.trim()}`;
                          return (
                            <p key={cid} className="text-xs text-slate-700" data-testid={`event-title-preview-${cid}`}>
                              <span className="text-slate-400">{cal?.summary || cid}:</span> {full}
                            </p>
                          );
                        })}
                      </div>
                    )}
                    {recipient && (
                      <p className="text-xs text-slate-500 mt-1 italic">
                        Precompilato dal nominativo. Modificalo pure.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">Descrizione</Label>
                    <Textarea rows={4} value={descrizione} onChange={(e) => setDescrizione(e.target.value)} placeholder="Dettagli, note..." className="mt-1" data-testid="event-description" />
                  </div>

                  {/* Articoli da Posare del cliente CRM — popolato solo se recipient non è manuale */}
                  {recipient && recipient.source !== "manuale" && (
                    <div className="border border-slate-200 rounded-lg p-3 bg-blue-50/40">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-semibold flex items-center gap-2 text-blue-900">
                          <FileText className="w-4 h-4 text-blue-600" />
                          Articoli da Posare
                          {!articoliLoading && (
                            <span className="text-xs font-normal text-slate-500">
                              ({articoliCliente.length})
                            </span>
                          )}
                        </Label>
                        {articoliCommessaId && (
                          <a
                            href={`/commessa/${articoliCommessaId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                            data-testid="open-commessa-link"
                          >
                            Apri Commessa →
                          </a>
                        )}
                      </div>
                      {articoliLoading ? (
                        <p className="text-xs italic text-slate-500">Caricamento articoli...</p>
                      ) : articoliCliente.length === 0 ? (
                        <p className="text-xs italic text-slate-500" data-testid="articoli-empty">
                          Nessun articolo flaggato "da posare" nella Commessa di questo cliente. Vai in Dettaglio Commessa &gt; Articoli da Posare per selezionarli.
                        </p>
                      ) : (
                        <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="articoli-list">
                          {articoliCliente.map((art, idx) => {
                            const nome = art.nome_articolo || "(senza nome)";
                            const ddt = art.codice_ddt;
                            const dep = art.deposito;
                            const trasmesso = art.trasmesso;
                            const dataArrivo = art.data_arrivo_deposito;
                            return (
                              <div
                                key={art.id || `art-${idx}`}
                                className="bg-white border border-slate-200 rounded px-2 py-1.5 text-xs flex items-center gap-2 flex-wrap"
                                data-testid={`articolo-${idx}`}
                              >
                                <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${trasmesso ? "bg-emerald-500" : "bg-slate-300"}`} title={trasmesso ? "Trasmesso" : "Non trasmesso"} />
                                <span className="font-medium text-slate-800 truncate flex-1 min-w-0">{nome}</span>
                                {ddt && <span className="bg-amber-100 text-amber-800 font-mono text-[10px] px-1.5 py-0.5 rounded shrink-0">DDT: {ddt}</span>}
                                {dep && <span className="bg-slate-100 text-slate-700 text-[10px] px-1.5 py-0.5 rounded shrink-0">{dep}</span>}
                                {dataArrivo && <span className="text-[10px] text-slate-500 shrink-0">📅 {dataArrivo}</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <p className="text-[11px] text-slate-500 italic mt-2">
                        💡 Pesca dagli articoli flaggati "da posare" nella pagina Dettaglio Commessa del cliente. Il codice DDT è recuperato dalla sezione Articoli Ordinati.
                      </p>
                    </div>
                  )}

                  {/* Smaltimento Vecchio */}
                  <div className="border border-slate-200 rounded-lg p-3 bg-amber-50/40">
                    <Label className="text-sm font-semibold flex items-center gap-2 mb-2">
                      <Trash2 className="w-4 h-4 text-amber-600" />
                      Smaltimento Vecchio
                    </Label>
                    <div className="flex gap-4 mb-3">
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="radio"
                          name="smaltimento-previsto"
                          checked={smaltimento.previsto === true}
                          onChange={() => setSmaltimento((p) => ({ ...p, previsto: true }))}
                          className="w-4 h-4 accent-amber-600"
                          data-testid="smaltimento-previsto"
                        />
                        <span className="font-medium text-amber-700">Previsto</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="radio"
                          name="smaltimento-previsto"
                          checked={smaltimento.previsto === false}
                          onChange={() => setSmaltimento({ previsto: false, smaltitore_id: "", note: "" })}
                          className="w-4 h-4 accent-slate-500"
                          data-testid="smaltimento-non-previsto"
                        />
                        <span className="text-slate-700">Non Previsto</span>
                      </label>
                    </div>
                    {smaltimento.previsto && (
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs font-semibold">Smaltitore (dall'Anagrafica)</Label>
                          <select
                            value={smaltimento.smaltitore_id}
                            onChange={(e) => setSmaltimento((p) => ({ ...p, smaltitore_id: e.target.value }))}
                            className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-sm mt-1"
                            data-testid="smaltimento-select"
                          >
                            <option value="">— Seleziona smaltitore —</option>
                            {smaltitori.length === 0 && (
                              <option value="" disabled>Nessuna voce in Anagrafica. Aggiungine una nel box qui sopra.</option>
                            )}
                            {smaltitori.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.denominazione}{s.telefono ? ` · ${s.telefono}` : ""} ({s.tipo === "vettore" ? "vettore" : "posatore"})
                              </option>
                            ))}
                          </select>
                        </div>
                        <p className="text-xs text-amber-700 bg-amber-100 border border-amber-200 rounded p-2">
                          ✍️ Verrà aggiunto in fondo alla descrizione: <strong>"Previsto Smaltimento: Il Posatore chiami lo Smaltitore appena è possibile fare ritiro"</strong> + denominazione e telefono dello smaltitore selezionato.
                        </p>
                      </div>
                    )}
                    <div className="mt-3">
                      <Label className="text-xs font-semibold">Note aggiuntive <span className="text-slate-400 font-normal">(opz. — se compilato viene aggiunto alla descrizione)</span></Label>
                      <Textarea rows={2} value={smaltimento.note} onChange={(e) => setSmaltimento((p) => ({ ...p, note: e.target.value }))} placeholder="Es: ingresso retro, suonare al 2°..." className="mt-1" data-testid="smaltimento-note" />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button type="button" onClick={checkConflicts} disabled={saving} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="check-conflicts-btn">
                      <Clock className="w-4 h-4 mr-2" />
                      Verifica conflitti
                    </Button>
                    <Button type="button" onClick={() => createOrUpdate(false)} disabled={saving} data-state="colored" style={{ backgroundColor: "#2563eb" }} className="text-white hover:opacity-90" data-testid="create-event-btn">
                      <CalendarPlus className="w-4 h-4 mr-2" />
                      {saving ? (editId ? "Aggiornamento..." : "Creazione...") : (editId ? "Aggiorna Evento" : "Crea Evento")}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Eventi Recenti */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="w-5 h-5 text-slate-500" />
                    Eventi Creati di Recente
                    <span className="ml-auto text-xs text-slate-500 font-normal">({recent.length})</span>
                  </CardTitle>
                  <p className="text-xs text-slate-500 italic">Mostra gli eventi creati negli ultimi 30 giorni con data odierna o futura (non sospesi).</p>
                </CardHeader>
                <CardContent>
                  {recent.length === 0 ? (
                    <p className="text-sm italic text-slate-500">Nessun evento creato ancora. Creane uno sopra.</p>
                  ) : (
                    <div className="divide-y divide-slate-200">
                      {recent.map((ev) => (
                        <div key={ev.id} className="py-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2" data-testid={`recent-${ev.id}`}>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-900 truncate">{ev.titolo}</p>
                            <p className="text-xs text-slate-600 mt-0.5">
                              📅 {ev.data} · 🕒 {ev.ora_inizio}{ev.ora_fine ? ` - ${ev.ora_fine}` : ""} · 📌 {ev.calendar_ids?.length || 0} calendari
                            </p>
                            {ev.recipient?.email && (
                              <p className="text-xs text-slate-500 mt-0.5">✉️ {ev.recipient.email}</p>
                            )}
                            {ev.notifica_cliente_inviata_at && (
                              <p className="text-xs text-emerald-600 mt-0.5">
                                ✓ Notifica inviata il {fmtIt(ev.notifica_cliente_inviata_at)}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-1 shrink-0 flex-wrap">
                            <Button type="button" variant="outline" size="sm" onClick={() => editRecent(ev)} data-testid={`edit-${ev.id}`}>
                              <Edit className="w-3 h-3 mr-1" /> Riprogramma
                            </Button>
                            <Button type="button" size="sm" onClick={() => openNotifica(ev)} data-state="colored" style={{ backgroundColor: "#059669" }} className="text-white hover:opacity-90" data-testid={`notify-${ev.id}`}>
                              <Mail className="w-3 h-3 mr-1" /> Notifica
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => suspendEvent(ev)} disabled={suspending === ev.id} className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid={`suspend-${ev.id}`}>
                              <Pause className="w-3 h-3 mr-1" /> {suspending === ev.id ? "..." : "Sospendi"}
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => openCancelDialog(ev)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`delete-${ev.id}`}>
                              <Trash2 className="w-3 h-3 mr-1" /> Cancella
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Eventi Sospesi */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Archive className="w-5 h-5 text-amber-600" />
                    Eventi Sospesi
                    <span className="ml-auto text-xs text-slate-500 font-normal">({suspended.length})</span>
                  </CardTitle>
                  <p className="text-xs text-slate-500 italic">Eventi parcheggiati al 31/12/2030. I dati originali sono conservati: usa "Recupera" per riprogrammarli.</p>
                </CardHeader>
                <CardContent>
                  {suspended.length === 0 ? (
                    <p className="text-sm italic text-slate-500">Nessun evento sospeso.</p>
                  ) : (
                    <div className="divide-y divide-slate-200">
                      {suspended.map((ev) => {
                        const orig = ev.suspended_original || {};
                        return (
                          <div key={ev.id} className="py-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2" data-testid={`suspended-${ev.id}`}>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-900 truncate">{(orig.titolo || ev.titolo || "").replace("[SOSPESO] ", "")}</p>
                              <p className="text-xs text-amber-700 mt-0.5">
                                ⏸️ Sospeso il {fmtIt(ev.suspended_at)}
                              </p>
                              <p className="text-xs text-slate-600 mt-0.5">
                                📅 Data originale: {orig.data || "-"} · 🕒 {orig.ora_inizio || "-"}{orig.ora_fine ? ` - ${orig.ora_fine}` : ""} · 📌 {(orig.calendar_ids || []).length} calendari
                              </p>
                              {ev.recipient?.email && (
                                <p className="text-xs text-slate-500 mt-0.5">✉️ {ev.recipient.email}</p>
                              )}
                            </div>
                            <div className="flex gap-1 shrink-0 flex-wrap">
                              <Button type="button" size="sm" onClick={() => openResumeDialog(ev)} data-state="colored" style={{ backgroundColor: "#2563eb" }} className="text-white hover:opacity-90" data-testid={`resume-${ev.id}`}>
                                <PlayCircle className="w-3 h-3 mr-1" /> Recupera
                              </Button>
                              <Button type="button" variant="outline" size="sm" onClick={() => openCancelDialog(ev)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`delete-suspended-${ev.id}`}>
                                <Trash2 className="w-3 h-3 mr-1" /> Elimina
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Conflicts Dialog */}
        <Dialog open={showConflicts} onOpenChange={setShowConflicts}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Conflitti rilevati
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              <p className="text-sm text-slate-700">
                Trovati <strong>{conflicts.length}</strong> eventi sovrapposti. Crea comunque oppure cambia orario.
              </p>
              <ul className="divide-y divide-slate-200 border border-slate-200 rounded">
                {conflicts.map((c, idx) => (
                  <li key={idx} className="p-2 text-sm" data-testid={`conflict-${idx}`}>
                    <div className="font-medium text-slate-800">{c.summary}</div>
                    <div className="text-xs text-slate-500">
                      {calendarName(c.calendar_id)} · {c.start} → {c.end}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowConflicts(false)} data-testid="conflict-cancel-btn">
                Annulla
              </Button>
              <Button type="button" onClick={() => createOrUpdate(true)} disabled={saving} data-state="colored" style={{ backgroundColor: "#dc2626" }} className="text-white hover:opacity-90" data-testid="conflict-force-btn">
                <XCircle className="w-4 h-4 mr-2" />
                {saving ? "Creazione..." : "Crea comunque"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Notifica Cliente Dialog */}
        <Dialog open={notificaOpen} onOpenChange={setNotificaOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-700">
                <Mail className="w-5 h-5" />
                Notifica Cliente — Anteprima email
              </DialogTitle>
            </DialogHeader>
            {notificaLoading ? (
              <p className="text-sm italic text-slate-500 py-4">Caricamento anteprima...</p>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-semibold">Destinatario</Label>
                  <Input value={notificaDest} onChange={(e) => setNotificaDest(e.target.value)} className="mt-1" data-testid="notifica-dest" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Oggetto</Label>
                  <Input value={notificaSubject} onChange={(e) => setNotificaSubject(e.target.value)} className="mt-1" data-testid="notifica-subject" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Testo email</Label>
                  <div className="bg-white rounded border border-slate-300 mt-1 quill-wrapper">
                    <ReactQuill
                      theme="snow"
                      value={notificaBody}
                      onChange={setNotificaBody}
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'color': [] }, { 'background': [] }],
                          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                          [{ 'align': [] }],
                          ['link', 'blockquote'],
                          ['clean'],
                        ],
                      }}
                      style={{ minHeight: 200 }}
                    />
                  </div>
                  <style>{`.quill-wrapper .ql-editor { min-height: 180px; font-size: 14px; }`}</style>
                  <p className="text-xs text-slate-500 italic mt-1">
                    Variabili disponibili per il template: <code>{"{{nome}} {{cognome}} {{email}} {{telefono}} {{data}} {{ora_inizio}} {{ora_fine}} {{titolo}} {{descrizione}}"}</code>
                  </p>
                </div>
                <p className="text-xs text-slate-500 italic">
                  💡 L'email verrà inviata al cliente e una copia in automatico a info@showroomingenio.it
                </p>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setNotificaOpen(false)} data-testid="notifica-cancel-btn">
                Annulla
              </Button>
              <Button type="button" variant="outline" onClick={saveNotificaTemplate} disabled={savingTpl} className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid="notifica-save-template-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingTpl ? "Salvataggio..." : "Salva Template"}
              </Button>
              <Button type="button" onClick={sendNotifica} disabled={sendingNotifica || notificaLoading} data-state="colored" style={{ backgroundColor: "#2563eb" }} className="text-white hover:opacity-90" data-testid="notifica-send-btn">
                <Send className="w-4 h-4 mr-2" />
                {sendingNotifica ? "Invio..." : "Invia Notifica"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancella Evento Dialog */}
        <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="w-5 h-5" />
                Cancella Evento
              </DialogTitle>
            </DialogHeader>
            {cancelTarget && (
              <div className="space-y-3 text-sm">
                <p className="text-slate-700">
                  Stai per cancellare definitivamente l'evento <strong>{(cancelTarget.titolo || "").replace("[SOSPESO] ", "")}</strong>.
                </p>
                <p className="text-xs text-slate-500">
                  📅 {cancelTarget.data} · 🕒 {cancelTarget.ora_inizio || "-"} · verrà rimosso da {cancelTarget.calendar_ids?.length || 0} calendari Google.
                </p>
                <label className="flex items-start gap-2 cursor-pointer bg-amber-50 border border-amber-200 rounded p-2">
                  <input
                    type="checkbox"
                    checked={cancelNotify}
                    onChange={(e) => setCancelNotify(e.target.checked)}
                    disabled={!cancelTarget.recipient?.email}
                    className="w-4 h-4 accent-red-600 mt-0.5"
                    data-testid="cancel-notify-check"
                  />
                  <span className="text-sm">
                    <strong>Invia email di annullamento al cliente</strong>
                    {cancelTarget.recipient?.email ? (
                      <span className="block text-xs text-slate-600">→ {cancelTarget.recipient.email} · copia a showroomingenio@gmail.com</span>
                    ) : (
                      <span className="block text-xs text-red-600 italic">Nessuna email cliente associata all'evento</span>
                    )}
                  </span>
                </label>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setCancelOpen(false)} data-testid="cancel-dialog-abort">
                Annulla
              </Button>
              <Button type="button" onClick={confirmCancel} disabled={cancelling} data-state="colored" style={{ backgroundColor: "#dc2626" }} className="text-white hover:opacity-90" data-testid="cancel-dialog-confirm">
                <Trash2 className="w-4 h-4 mr-2" />
                {cancelling ? "Cancellazione..." : "Conferma Cancellazione"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Recupera Evento Sospeso Dialog */}
        <Dialog open={resumeOpen} onOpenChange={setResumeOpen}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-700">
                <PlayCircle className="w-5 h-5" />
                Recupera Evento Sospeso
              </DialogTitle>
            </DialogHeader>
            {resumeTarget && (
              <div className="space-y-3 text-sm">
                <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs">
                  <p className="font-medium text-slate-800">{((resumeTarget.suspended_original || {}).titolo || resumeTarget.titolo || "").replace("[SOSPESO] ", "")}</p>
                  <p className="text-slate-600 mt-0.5">
                    📅 Data originale: {(resumeTarget.suspended_original || {}).data || "-"} · 🕒 {(resumeTarget.suspended_original || {}).ora_inizio || "-"}
                  </p>
                  {resumeTarget.recipient?.email && (
                    <p className="text-slate-600 mt-0.5">✉️ {resumeTarget.recipient.email}</p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs font-semibold">Nuova data *</Label>
                    <Input type="date" value={resumeData} onChange={(e) => setResumeData(e.target.value)} className="mt-1" data-testid="resume-date" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Ora inizio *</Label>
                    <Input type="time" value={resumeOraInizio} onChange={(e) => setResumeOraInizio(e.target.value)} className="mt-1" data-testid="resume-time-start" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Ora fine</Label>
                    <Input type="time" value={resumeOraFine} onChange={(e) => setResumeOraFine(e.target.value)} className="mt-1" data-testid="resume-time-end" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Calendari su cui riprogrammare</Label>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-slate-200 rounded p-2 bg-slate-50">
                    {calendars.filter((c) => c.access_role === "owner" || c.access_role === "writer").map((c) => (
                      <label key={c.id} className="flex items-center gap-2 cursor-pointer bg-white px-2 py-1 rounded border border-slate-200 text-xs">
                        <input
                          type="checkbox"
                          checked={resumeCalendars.includes(c.id)}
                          onChange={() => toggleResumeCalendar(c.id)}
                          className="w-4 h-4 accent-blue-600"
                          data-testid={`resume-cal-${c.id}`}
                        />
                        <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c.background_color || "#64748b" }} />
                        <span className="text-slate-800 truncate">{c.summary}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 italic mt-1">
                    💡 Se aggiungi/togli calendari rispetto all'originale, l'evento verrà eliminato dai calendari rimossi e creato nei nuovi.
                  </p>
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setResumeOpen(false)} data-testid="resume-cancel-btn">
                Annulla
              </Button>
              <Button type="button" onClick={confirmResume} disabled={resuming} data-state="colored" style={{ backgroundColor: "#2563eb" }} className="text-white hover:opacity-90" data-testid="resume-confirm-btn">
                <PlayCircle className="w-4 h-4 mr-2" />
                {resuming ? "Recupero..." : "Riprogramma Evento"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
